import React from "react";
import { Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, FileText } from "lucide-react";
import { Button } from "../../components/ui/button";
import { BACKEND_URL } from "../../config";
import { useMusicPlayer } from "../../contexts/AppContext";

export default function MusicPlayer() {
  const { currentTrack, isPlaying, progress, duration, volume, isMuted, currentAlbum, showLyrics, togglePlay, nextTrack, prevTrack, seekTo, setVolume, toggleMute, toggleLyrics } = useMusicPlayer();
  if (!currentTrack) return null;

  const formatTime = (time) => {
    if (!time || isNaN(time)) return "0:00";
    return `${Math.floor(time / 60)}:${Math.floor(time % 60).toString().padStart(2, '0')}`;
  };

  return (
    <div data-testid="music-player" className="fixed bottom-0 left-0 right-0 z-40 glass border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 min-w-0 flex-1 md:flex-none md:w-64">
            {currentAlbum && <img src={`${BACKEND_URL}/storage/${currentAlbum.cover_image}`} alt={currentAlbum.title} className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover" />}
            <div className="min-w-0">
              <p className="font-medium text-xs sm:text-sm truncate">{currentTrack.title}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{currentAlbum?.title}</p>
            </div>
          </div>

          <div className="flex-1 hidden md:flex flex-col items-center gap-2">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={prevTrack} className="h-8 w-8"><SkipBack className="h-4 w-4" /></Button>
              <Button variant="default" size="icon" onClick={togglePlay} className="h-10 w-10 rounded-full bg-primary hover:bg-primary/90">
                {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={nextTrack} className="h-8 w-8"><SkipForward className="h-4 w-4" /></Button>
            </div>
            <div className="flex items-center gap-2 w-full max-w-md">
              <span className="text-xs text-muted-foreground w-10 text-right">{formatTime(progress)}</span>
              <input type="range" min="0" max={duration || 100} value={progress} onChange={(e) => seekTo(Number(e.target.value))} className="progress-bar flex-1" />
              <span className="text-xs text-muted-foreground w-10">{formatTime(duration)}</span>
            </div>
          </div>

          <Button variant="default" size="icon" onClick={togglePlay} className="md:hidden h-10 w-10 rounded-full bg-primary hover:bg-primary/90">
            {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
          </Button>

          <div className="hidden md:flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleLyrics} className={`h-8 w-8 ${showLyrics ? 'text-primary' : ''}`} data-testid="lyrics-toggle">
              <FileText className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={toggleMute} className="h-8 w-8">
              {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </Button>
            <input type="range" min="0" max="1" step="0.1" value={isMuted ? 0 : volume} onChange={(e) => setVolume(Number(e.target.value))} className="progress-bar w-20" />
          </div>
        </div>
      </div>
    </div>
  );
}
